import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Checkbox } from '@uhg-abyss/web/ui/Checkbox';
import { styled } from '@uhg-abyss/web/tools/styled';
import { IconSymbol } from '@uhg-abyss/web/ui/IconSymbol';
import { Flex } from '@uhg-abyss/web/ui/Flex';
import { TextInput } from '@uhg-abyss/web/ui/TextInput';
import { useForm } from '@uhg-abyss/web/hooks/useForm';
import { useEditing } from '@src/state/editing/hooks';
import { event as adobeTaggingEvent, event } from '@uhg-abyss/web/tools/event';
import { useTIN } from '@src/state/taxInformation';
import { useUser } from '@src/state/user';
import { useOnloading } from '@src/state/landing';
import { FormProvider } from '@uhg-abyss/web/ui/FormProvider';
import { RadioGroup } from '@uhg-abyss/web/ui/RadioGroup';
import { Tooltip } from '@uhg-abyss/web/ui/Tooltip';
import { Button } from '@uhg-abyss/web/ui/Button';
import { LagoonContext } from '@src/AppContext/LagoonContext';
import { getOrgType } from '../helpers';

const MainContainer = styled('div', {
  border: '1px solid rgb(219, 219, 219)',
  background: '$white',
  padding: '25px',
  marginBottom: '20px',
});

const BoxHeader = styled('div', {
  position: 'relative',
  marginBottom: '5px',
});

const BoxHeaderText = styled('h3', {
  fontWeight: 600,
  fontSize: '24px',
  width: '100%',
  color: '$primary1',
  fontFamily: '$heading',
});

const Description = styled('p', {
  fontSize: '16px',
  fontWeight: 500,
  marginBottom: '30px',
  fontFamily: '$primary',
});

const JobDescription = styled('p', {
  fontSize: '14px',
  fontWeight: 500,
  marginBottom: '20px',
  paddingLeft: '36px',
  lineHeight: 1.25,
  marginTop: '8px',
  fontFamily: '$primary',
});
const TextAreaLabel = styled('div', {
  justifyContent: 'space-between',
});

const JobName = styled('p', {
  fontSize: '16px',
  fontWeight: 500,
  marginTop: '-3px',
  marginBottom: '0px',
  paddingLeft: '5px',
  fontFamily: '$primary',
});

const BackButton = styled('div', {
  width: '115px',
  padding: '6px',
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'center',
  borderWidth: '2.5px',
  border: ' 2px solid $primary1',
  background: '$white',
  borderStyle: 'solid',
  borderRadius: '23px',
  marginTop: 'auto',
  marginBottom: '20px',
  alignItems: 'center',
  '&:hover': {
    cursor: 'pointer',
  },
});

const NextButton = styled('div', {
  float: 'right',
  width: '115px',
  padding: '6px',
  display: 'flex',
  justifyContent: 'center',
  flexDirection: 'row',
  borderWidth: '2.5px',
  border: '2px solid $primary1',
  background: '$primary1',
  borderStyle: 'solid',
  borderRadius: '23px',
  marginTop: 'auto',
  marginBottom: '20px',
  alignItems: 'center',
  '&:hover': {
    cursor: 'pointer',
  },
});

const BackButtonText = styled('text', {
  color: '$primary1',
  fontWeight: 'bold',
  marginLeft: '12px',
  fontSize: '16px',
});

const NextButtonText = styled('text', {
  color: '$white',
  fontWeight: 'bold',
  marginRight: '12px',
  fontSize: '16px',
});

const ClinicalStaffLabel = styled('p', {
  fontSize: '14px',
  fontWeight: 600,
  marginBottom: 0,
  fontFamily: '$primary',
});

const FormErrorMessage = styled('span', {
  fontWeight: 600,
  fontSize: '80%',
  width: '100%',
  color: 'red',
  display: 'inline-block',
  marginTop: '5px',
});
const InvalidNpiDiv = styled('div', {
  color: '$redDvz1',
  fontSize: '13px',
  marginTop: '2px',
});
const MandatorySpan = styled('span', {
  fontSize: '16px',
  color: '$redDvz1',
});

const JobView = ({
  id,
  name,
  desc,
  isNpiRequired,
  onChecked,
  onNpiChanged,
  currentState,
  npiValueRequired,
  onCompanyChange,
  onRCMChanged,
  companyNameRequired,
  RCMRadioValueRequired,
  isAPIMarketPlaceUser,
}) => {
  const [npiRequired, setNpiRequired] = useState(false);
  const form = useForm();
  const [selectedNpiId, setSelectedNpiId] = useState(0);
  const [npiLength, setNpiLength] = useState(0);
  const [RCMRadioValue, setRCMRadioValue] = useState('');
  const [adminSelected, setAdminSelected] = useState(false);

  const lagoonContext = React.useContext(LagoonContext);
  const featureToggles = lagoonContext.getTable('feature-toggles');

  const handleChecked = (e) => {
    if (e.target.checked && isNpiRequired) {
      setNpiRequired(true);
    } else {
      setNpiRequired(false);
      form.setValue(`SURegistration.job_npi_txt_${id}`, '');
    }

    setAdminSelected(e.target.checked && name === 'Administrative Staff');
    onChecked(id, e.target.checked);
  };
  const handleNpiChange = (inp) => {
    setNpiLength(inp.length);
  };

  const handleRCMChange = (value) => {
    if (featureToggles.getRow('ADOBE_ANALYTICS_TRACKING')?.active) {
      event('RCM_SELECTION', {
        interactionName:
          'Working on behalf of a Revenue Cycle Management (RCM) or other billing company',
        interactionValue: value === 'Y' ? 'Yes' : 'No',
        interactionContext: 'Job Functions',
      });
    }

    setRCMRadioValue(value);
    if (value === 'N') {
      form.setValue(`SURegistration.job_company_txt_${id}`, '');
      onCompanyChange(id, '');
    }
  };

  const handleCompanyName = (e) => {
    onCompanyChange(id, e.target.value);
  };

  useEffect(() => {
    const {
      checked,
      npiValue,
      id: presentid,
      RCMValue,
      code,
      companyName,
    } = currentState || {
      checked: false,
      npiValue: '',
    };
    setSelectedNpiId(presentid);
    form.setValue(`SURegistration.job_cbox_${id}`, checked);
    if (isNpiRequired && (npiValue || checked)) {
      form.setValue(`SURegistration.job_npi_txt_${id}`, npiValue);
      if (npiValue && npiValue.length > 0 && npiValue.length < 10) {
        handleNpiChange(npiValue);
      }
      setNpiRequired(true);
    } else if (code === 'AS' && checked) {
      setAdminSelected(true);
      form.setValue(`SURegistration.job_radio_${id}`, RCMValue);
      form.setValue(`SURegistration.job_company_txt_${id}`, companyName);
      setRCMRadioValue(RCMValue);
    } else {
      form.setValue(`SURegistration.job_npi_txt_${id}`, '');
      setNpiLength(0);
      setNpiRequired(false);
      form.setValue(`SURegistration.job_radio_${id}`, '');
      form.setValue(`SURegistration.job_company_txt_${id}`, '   ');
    }
  }, [currentState]);
  const labelText = () => {
    return (
      <TextAreaLabel>
        National Provider Identification #{' '}
        {id === selectedNpiId && <MandatorySpan>*</MandatorySpan>}
      </TextAreaLabel>
    );
  };
  return (
    <FormProvider state={form} style={{ marginBottom: '20px' }}>
      <Checkbox
        data-testid={`job_cbox_${id}`}
        id={`job_cbox_${id}`}
        model={`SURegistration.job_cbox_${id}`}
        size="sm"
        onChange={handleChecked}
        aria-label=""
      >
        <span>
          <JobName>{name}</JobName>
        </span>
      </Checkbox>
      <JobDescription>{desc}</JobDescription>
      {adminSelected && !isAPIMarketPlaceUser && (
        <div
          style={{
            marginLeft: '36px',
          }}
        >
          <RadioGroup
            id={`job_radio_${id}`}
            label="Are you working on behalf of a Revenue Cycle Management (RCM) or other billing company?"
            model={`SURegistration.job_radio_${id}`}
            onChange={(e) => {
              handleRCMChange(e.target.value);
              onRCMChanged(id, e.target.value);
            }}
            display="row"
            validators={{ required: true }}
          >
            <RadioGroup.Radio label="Yes" value="Y" />
            <RadioGroup.Radio label="No" value="N" />
          </RadioGroup>
          {RCMRadioValueRequired && (
            <InvalidNpiDiv>Please select Yes or No</InvalidNpiDiv>
          )}
          <TextInput
            id="companyname-input"
            label="Company Name"
            model={`SURegistration.job_company_txt_${id}`}
            aria-label="type here to enter company name"
            validators={{ required: RCMRadioValue === 'Y' }}
            maxLength={50}
            onChange={(e) => {
              return handleCompanyName(e);
            }}
            isDisabled={RCMRadioValue !== 'Y'}
            css={{
              'abyss-text-input-root': { width: '350px' },
            }}
            onClear={() => {
              return onCompanyChange(id, '');
            }}
            isClearable
            helper={
              <Tooltip content="Identifying RCM/billing company associates will better allow us to tailor your UHC digital experience.">
                <Button
                  css={{
                    'abyss-button-root': { height: '14px', width: '14px' },
                  }}
                  aria-label="Tooltip information"
                  rounded
                >
                  <IconSymbol icon="question_mark" color="$white" size={12} />
                </Button>
              </Tooltip>
            }
          />
          {companyNameRequired && (
            <InvalidNpiDiv>Please enter Company Name</InvalidNpiDiv>
          )}
        </div>
      )}
      {isNpiRequired ? (
        <div
          style={{
            marginLeft: '36px',
            width: '235px',
            marginBottom: '30px',
            marginTop: '-10px',
          }}
        >
          <TextInput
            id={`job_ctrl_npi_text_${id}`}
            model={`SURegistration.job_npi_txt_${id}`}
            label={labelText()}
            aria-label=""
            maxLength={10}
            css={{
              'abyss-text-input-label': {
                fontSize: '14px',
              },
            }}
            formatter={(value) => {
              return value.replace(/[^0-9.]/g, '');
            }}
            onChange={(e) => {
              handleNpiChange(e.target.value);
              onNpiChanged(id, e.target.value);
              form.validate(`SURegistration.job_npi_txt_${id}`);
            }}
            onClear={() => {
              return onNpiChanged(id, '');
            }}
            isDisabled={!npiRequired}
            isClearable
          />
          {id === selectedNpiId &&
          ((npiLength > 0 && npiLength < 10) || npiValueRequired) ? (
            <InvalidNpiDiv>Invalid NPI value</InvalidNpiDiv>
          ) : (
            ''
          )}
        </div>
      ) : null}
    </FormProvider>
  );
};

export const JobFunction = ({
  jobFunctions,
  handleBack,
  selectedJobFunctions,
  setJobFunctions,
  handleNext,
}) => {
  const lagoonContext = React.useContext(LagoonContext);
  const featureToggles = lagoonContext.getTable('feature-toggles');
  const [adminJobs, setAdminJobs] = useState([]);
  const [nonAdminJobs, setNonAdminJobs] = useState([]);
  const [jobSelection, setJobSelection] = useState({});
  const [formErrorMessage, setFormErrorMessage] = useState('');
  const form = useForm();
  const { isEditing } = useEditing.state();
  const { TINNumber, TINInfo } = useTIN.state();
  const { userUUID, isAPIMarketPlaceUser } = useUser.state();
  const { currentFlow } = useOnloading.state();
  const [npiValueRequired, setNpiValueRequired] = useState(false);
  const [companyNameRequired, setCompanyNameRequired] = useState(false);
  const [RCMRadioValueRequired, setRCMRadioValueRequired] = useState(false);

  useEffect(() => {
    if (!jobFunctions) return;
    jobFunctions.sort((j1, j2) => {
      return j1.order - j2.order;
    });
    setAdminJobs(
      jobFunctions.filter((j) => {
        return j.category === 'Administrative Staff';
      })
    );
    setNonAdminJobs(
      jobFunctions.filter((j) => {
        return j.category !== 'Administrative Staff';
      })
    );

    if (!selectedJobFunctions) return;

    const jobs = {};

    Object.keys(selectedJobFunctions).forEach((key) => {
      const j = selectedJobFunctions[key];
      if (!jobs[j.id]) jobs[j.id] = { ...j };
    });

    setJobSelection(jobs);
  }, [jobFunctions]);

  useEffect(() => {
    if (featureToggles.getRow('ADOBE_ANALYTICS_TRACKING')?.active) {
      const orgType = getOrgType(currentFlow);
      let section1;
      let section2;
      let section3 = '';
      let pageName;
      if (!TINInfo?.hasPAA) {
        section1 = 'paa registration';
        section2 = 'job function';
        pageName = `digitalsecurity:${section1}:${section2}`;
      } else {
        section1 = 'tin registration';
        section2 = orgType;
        section3 = 'job function';
        pageName = `digitalsecurity:${section1}:${section2}:${section3}`;
      }
      adobeTaggingEvent('ON_PAGE_LOAD', {
        pageName,
        orgType,
        organization: TINInfo?.corpName,
        uuid: userUUID,
        tin: TINNumber,
        section1,
        section2,
        section3,
      });
    }
  }, []);

  const findJobById = (id) => {
    for (let j = 0; j < jobFunctions.length; j++) {
      if (jobFunctions[j].id === id) {
        return jobFunctions[j];
      }
    }
    return null;
  };

  const onJobChecked = (id, checked) => {
    if (!checked && jobSelection[id]) delete jobSelection[id];
    if (checked && !jobSelection[id])
      jobSelection[id] = { ...findJobById(id), checked: true };
    if (checked && jobSelection[id].isNpiRequired) {
      const npiFieldToUncheck = Object.values(jobSelection).find((j) => {
        return j.isNpiRequired && j.name !== jobSelection[id].name;
      });
      if (npiFieldToUncheck) {
        jobSelection[id].npiValue = npiFieldToUncheck.npiValue;
        delete jobSelection[npiFieldToUncheck.id];
      }
    }

    setJobSelection({ ...jobSelection });
    setFormErrorMessage('');
    if (npiValueRequired) setNpiValueRequired(false);
  };

  const onNpiValueChanged = (id, value) => {
    if (!jobSelection[id]) jobSelection[id] = { ...findJobById(id) };
    jobSelection[id].npiValue = value;
    setFormErrorMessage('');
    if (npiValueRequired) setNpiValueRequired(false);
  };

  const onCompanyChange = (id, value) => {
    if (!jobSelection[id]) jobSelection[id] = { ...findJobById(id) };
    jobSelection[id].companyName = value;
    setFormErrorMessage('');
    if (companyNameRequired) setCompanyNameRequired(false);
  };

  const onRCMChange = (id, value) => {
    if (!jobSelection[id]) jobSelection[id] = { ...findJobById(id) };
    jobSelection[id].RCMValue = value;
    setFormErrorMessage('');
    if (RCMRadioValueRequired) setRCMRadioValueRequired(false);
  };

  const isFormValid = () => {
    let valid = true;

    Object.keys(jobSelection).forEach((jobId) => {
      if (
        jobSelection[jobId].checked &&
        jobSelection[jobId].isNpiRequired &&
        (!jobSelection[jobId].npiValue ||
          !jobSelection[jobId].npiValue.match(/^\d{10}$/))
      ) {
        form.validate(`SURegistration.job_npi_txt_${jobId}`);
        setNpiValueRequired(true);
        valid = false;
      } else if (
        jobSelection[jobId].checked &&
        jobSelection[jobId].code === 'AS' &&
        !isAPIMarketPlaceUser
      ) {
        if (!jobSelection[jobId].RCMValue) {
          setRCMRadioValueRequired(true);
          valid = false;
          if (featureToggles.getRow('ADOBE_ANALYTICS_TRACKING')?.active) {
            const fields = ['RCM Indicator not selecteed'];
            adobeTaggingEvent('FORM_VALIDATION_ERROR', {
              fields,
              pageName: `digitalsecurity:${
                !TINInfo?.hasPAA ? 'paa registration' : 'tin registration'
              }:job function`,
            });
          }
        } else if (
          jobSelection[jobId].RCMValue === 'Y' &&
          !jobSelection[jobId].companyName
        ) {
          setCompanyNameRequired(true);
          valid = false;
          if (featureToggles.getRow('ADOBE_ANALYTICS_TRACKING')?.active) {
            const fields = ['RCM Company name missing'];
            adobeTaggingEvent('FORM_VALIDATION_ERROR', {
              fields,
              pageName: `digitalsecurity:${
                !TINInfo?.hasPAA ? 'paa registration' : 'tin registration'
              }:job function`,
            });
          }
        }
      }
    });

    return valid;
  };

  const onNext = () => {
    const noJobSelected = Object.keys(jobSelection).length === 0;
    if (noJobSelected) {
      if (featureToggles.getRow('ADOBE_ANALYTICS_TRACKING')?.active) {
        const fields = ['Job Functions - no job function selected'];
        adobeTaggingEvent('FORM_VALIDATION_ERROR', { fields });
      }
      return setFormErrorMessage('Please select at least one job function.');
    }

    if (!isFormValid()) {
      const jobs = Object.values(jobSelection);
      const jobMissingNPI =
        jobs.filter((element) => {
          return element.isNpiRequired;
        })[0]?.name || '';
      if (featureToggles.getRow('ADOBE_ANALYTICS_TRACKING')?.active) {
        const fields = [`${jobMissingNPI} NPI Field`];
        adobeTaggingEvent('FORM_VALIDATION_ERROR', { fields });
      }
      return setFormErrorMessage('Please correct the above error(s).');
    }

    handleNext();
    return setJobFunctions(jobSelection);
  };

  return (
    <React.Fragment>
      <MainContainer>
        <BoxHeader>
          <BoxHeaderText>
            Select the job function(s) that most closely aligns to your role.
          </BoxHeaderText>
        </BoxHeader>
        <Description>
          Please note: Your selection will not change your access or affect the
          functionality of the portal.
        </Description>
        {adminJobs.map((job) => {
          return (
            <JobView
              key={job.id}
              {...job}
              currentState={jobSelection[job.id]}
              onChecked={onJobChecked}
              onNpiChanged={onNpiValueChanged}
              npiValueRequired={npiValueRequired}
              onCompanyChange={onCompanyChange}
              onRCMChanged={onRCMChange}
              companyNameRequired={companyNameRequired}
              RCMRadioValueRequired={RCMRadioValueRequired}
              isAPIMarketPlaceUser={isAPIMarketPlaceUser}
            />
          );
        })}
        {nonAdminJobs.length >= 1 ? (
          <ClinicalStaffLabel>Clinical Staff</ClinicalStaffLabel>
        ) : null}
        {nonAdminJobs.map((job) => {
          return (
            <JobView
              key={job.id}
              {...job}
              currentState={jobSelection[job.id]}
              onChecked={onJobChecked}
              onNpiChanged={onNpiValueChanged}
              npiValueRequired={npiValueRequired}
            />
          );
        })}
        {formErrorMessage ? (
          <FormErrorMessage>{formErrorMessage}</FormErrorMessage>
        ) : null}
      </MainContainer>
      <Flex
        css={{
          'abyss-flex-root': {
            justifyContent: 'space-between',
          },
        }}
      >
        <Flex.Content space-50>
          {!isEditing && (
            <BackButton onClick={handleBack}>
              <IconSymbol icon="arrow_back" size={20} color="$primary1" />
              <BackButtonText>Back</BackButtonText>
            </BackButton>
          )}
        </Flex.Content>
        <Flex.Content space-50>
          <NextButton onClick={onNext}>
            <NextButtonText>{isEditing ? 'Save' : 'Next'}</NextButtonText>
            <IconSymbol icon="arrow_forward" size={16} color="$primary2" />
          </NextButton>
        </Flex.Content>
      </Flex>
    </React.Fragment>
  );
};

JobFunction.propTypes = {
  jobFunctions: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.string })),
  selectedJobFunctions: PropTypes.shape({}),
  handleBack: PropTypes.func.isRequired,
  setJobFunctions: PropTypes.func.isRequired,
  handleNext: PropTypes.func.isRequired,
};

JobView.propTypes = {
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  desc: PropTypes.string.isRequired,
  isNpiRequired: PropTypes.bool.isRequired,
  currentState: PropTypes.shape({
    checked: PropTypes.bool.isRequired,
    npiValue: PropTypes.string,
  }),
  onChecked: PropTypes.func.isRequired,
  onNpiChanged: PropTypes.func.isRequired,
  npiValueRequired: PropTypes.bool.isRequired,
  onCompanyChange: PropTypes.func,
  onRCMChanged: PropTypes.func,
  companyNameRequired: PropTypes.bool,
  RCMRadioValueRequired: PropTypes.bool,
  isAPIMarketPlaceUser: PropTypes.bool,
};

JobView.defaultProps = {
  currentState: null,
  companyNameRequired: false,
  RCMRadioValueRequired: false,
  onCompanyChange: () => {},
  onRCMChanged: () => {},
  isAPIMarketPlaceUser: false,
};

JobFunction.defaultProps = {
  jobFunctions: [],
  selectedJobFunctions: [],
};
